// ── LEVEL OF EFFORT TAB ──────────────────────────────────────
// Compares two months of staff Level of Effort — how each person's time is
// split across grants — and lists who joined, who left, whose grants or
// percentages changed, and rows whose written allocation disagrees with the
// grid. Two files, or one workbook with both months as tabs. The rules live
// in lib/loe.js and are configurable per organisation.
import { useState } from "react";
import { FONT_UI, FONT_MONO } from "../theme";
import { API, apiFetch } from "../utils/api";

const TYPE_LABEL  = { new: "New staff", departed: "Departed", grant_change: "Grant change", loe_change: "LoE % change", review: "Review", mismatch: "Text/grid mismatch" };
const TYPE_COLOUR = { new: "#059669", departed: "#dc2626", grant_change: "#d97706", loe_change: "#2563eb", review: "#7c3aed", mismatch: "#db2777" };
const RED = "#dc2626", AMBER = "#d97706";

function saveFile(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
// Allocations may arrive as text ("GC7 60%, SWANN 40%") or as {grant: pct}.
const show = (v) => (v == null ? "" : typeof v === "object" ? Object.entries(v).map(([g, p]) => `${g} ${p}%`).join(", ") : String(v));
const count = (x) => (Array.isArray(x) ? x.length : x);

export default function LoeTab({ T, card, label, btn }) {
  const [mode, setMode]     = useState("two");
  const [prev, setPrev]     = useState(null);
  const [curr, setCurr]     = useState(null);
  const [one, setOne]       = useState(null);
  const [report, setReport] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy]     = useState("");
  const [error, setError]   = useState("");

  const th = { textAlign: "left", padding: "7px 8px", fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: T.textMuted, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" };
  const td = { padding: "7px 8px", fontFamily: FONT_UI, fontSize: 12.5, color: T.textBody, borderBottom: `1px solid ${T.border}`, verticalAlign: "top" };
  const title = { fontFamily: FONT_UI, fontSize: 15, fontWeight: 700, color: T.text, margin: "0 0 4px" };
  const sub = { fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted, margin: "0 0 14px", lineHeight: 1.55 };
  const note = (c) => ({ padding: "10px 12px", borderRadius: 8, fontFamily: FONT_UI, fontSize: 12.5, lineHeight: 1.55, background: `${c}14`, border: `1px solid ${c}55`, color: T.textBody, marginTop: 10 });

  const ready = mode === "two" ? Boolean(prev && curr) : Boolean(one);
  const form = () => {
    const fd = new FormData();
    if (mode === "two") { fd.append("previous", prev); fd.append("current", curr); } else fd.append("workbook", one);
    return fd;
  };

  const run = async () => {
    setBusy("run"); setError(""); setReport(null);
    try {
      const r = await apiFetch(`${API}/loe/compare`, { method: "POST", body: form() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Server error ${r.status}`);
      setReport(d.report); setFilter("all");
    } catch (e) { setError(e.message); }
    setBusy("");
  };

  const download = async () => {
    setBusy("download"); setError("");
    try {
      const r = await apiFetch(`${API}/loe/compare?format=xlsx`, { method: "POST", body: form() });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `Server error ${r.status}`); }
      saveFile(await r.blob(), "LoE Change Report.xlsx");
    } catch (e) { setError(e.message); }
    setBusy("");
  };

  const FilePick = ({ text, file, onPick }) => (
    <label style={{ ...btn(false), display: "inline-flex", alignItems: "center", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {file ? `✓ ${file.name}` : text}
      <input type="file" accept=".xlsx,.xlsm,.xls" hidden onChange={(e) => { onPick(e.target.files?.[0] || null); setReport(null); e.target.value = ""; }} />
    </label>
  );

  const rows = report ? report.rows.filter((r) => filter === "all" || r.type === filter) : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={card}>
        <div style={title}>Level of Effort: month-to-month comparison</div>
        <p style={sub}>Upload last month's and this month's staff Level of Effort. NofoDesk lists new and departed staff, grant changes, percentage changes, and rows where the written allocation and the grid disagree.</p>
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {[["two", "Two files"], ["one", "One workbook, both months as tabs"]].map(([id, text]) => (
            <button key={id} onClick={() => { setMode(id); setReport(null); }} style={{ ...btn(mode === id), padding: "7px 13px", minHeight: 32 }}>{text}</button>
          ))}
        </div>
        {mode === "two" ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <div><span style={label}>Previous month</span><FilePick text="Choose file" file={prev} onPick={setPrev} /></div>
            <div><span style={label}>Current month</span><FilePick text="Choose file" file={curr} onPick={setCurr} /></div>
          </div>
        ) : (
          <div><span style={label}>Workbook</span><FilePick text="Choose workbook" file={one} onPick={setOne} /></div>
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <button style={{ ...btn(true), opacity: ready && !busy ? 1 : 0.5 }} disabled={!ready || !!busy} onClick={run}>{busy === "run" ? "Comparing…" : "Compare"}</button>
          {report && <button style={btn(false)} disabled={!!busy} onClick={download}>{busy === "download" ? "Preparing…" : "Download report (.xlsx)"}</button>}
        </div>
        {error && <div style={note(RED)}>{error}</div>}
      </div>

      {report && (
        <div style={card}>
          <div style={title}>{report.previous?.label} → {report.current?.label}</div>
          <p style={{ ...sub, marginBottom: 10 }}>{count(report.previous?.staff)} staff before · {count(report.current?.staff)} now · {report.rows.length} change{report.rows.length === 1 ? "" : "s"}</p>
          {report.swapped && <div style={{ ...note(AMBER), marginTop: 0, marginBottom: 10 }}>The files looked the wrong way round (the "previous" file is the later month), so NofoDesk swapped them.</div>}
          {report.sameMonth && <div style={{ ...note(AMBER), marginTop: 0, marginBottom: 10 }}>Both files appear to be the same month. Check you chose the right files.</div>}
          {(report.grants?.introduced?.length > 0 || report.grants?.ended?.length > 0) && (
            <p style={{ ...sub, marginBottom: 10 }}>
              {report.grants.introduced?.length > 0 && <>New grants: <b>{report.grants.introduced.join(", ")}</b>. </>}
              {report.grants.ended?.length > 0 && <>No longer charged: <b>{report.grants.ended.join(", ")}</b>.</>}
            </p>
          )}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
            <button onClick={() => setFilter("all")} style={{ ...btn(filter === "all"), padding: "5px 11px", minHeight: 28, fontSize: 12 }}>All {report.rows.length}</button>
            {Object.entries(TYPE_LABEL).filter(([k]) => report.summary?.[k]).map(([k, text]) => (
              <button key={k} onClick={() => setFilter(k)} style={{ ...btn(false), padding: "5px 11px", minHeight: 28, fontSize: 12, borderColor: TYPE_COLOUR[k], color: filter === k ? "#fff" : TYPE_COLOUR[k], background: filter === k ? TYPE_COLOUR[k] : "transparent" }}>
                {text} {report.summary[k]}
              </button>
            ))}
          </div>
          {rows.length === 0 ? (
            <p style={sub}>No changes between the two months.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>Change</th><th style={th}>Staff ID</th><th style={th}>Name</th><th style={th}>Department</th><th style={th}>Before</th><th style={th}>Now</th><th style={th}>Note</th></tr></thead>
                <tbody>{rows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...td, color: TYPE_COLOUR[r.type], fontWeight: 600, whiteSpace: "nowrap" }}>{TYPE_LABEL[r.type] || r.type}</td>
                    <td style={{ ...td, fontFamily: FONT_MONO }}>{r.staffId}</td>
                    <td style={td}>{[r.surname, r.otherNames].filter(Boolean).join(", ")}</td>
                    <td style={td}>{r.department}</td>
                    <td style={td}>{show(r.previous)}</td>
                    <td style={td}>{show(r.current)}</td>
                    <td style={td}>{r.note}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
